import React, { useState } from 'react';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    AppBar,
    Toolbar,
    useMediaQuery,
    Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArticleIcon from '@mui/icons-material/Article';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleIcon from '@mui/icons-material/People';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clearSession } from '../../utils/session';
import './admin.scss';

const DRAWER_WIDTH = 250;
const MOBILE_DRAWER_WIDTH = 250;
const COLLAPSED_DRAWER_WIDTH = 84;
const NEOFEED_AI_LOGO_SRC = '/neofeedAI.png';
const COMPACT_LOGO_SRC = '/neofeed-n.png';

export default function AdminLayout({ children, fullWidth = false }) {
    const isMobile = useMediaQuery('(max-width:1024px)');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const MENU_ITEMS = [
        { label: 'Dashboard', icon: <BarChartIcon />, path: '/admin/dashboards' },
        { label: 'Inteligência Editorial', icon: <ArticleIcon />, path: '/admin/editorial-intelligence' },
        { label: 'Matérias', icon: <NewspaperIcon />, path: '/admin/articles' },
        { label: 'Usuários', icon: <PeopleIcon />, path: '/admin/config/users' },
        { label: 'Manual do Usuário', icon: <MenuBookOutlinedIcon />, path: '/admin/user-manual' },
        { label: 'Suporte', icon: <SupportAgentIcon />, path: '/admin/support' },
    ];

    const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

    const handleLogout = () => {
        clearSession();
        navigate('/admin/login');
    };

    const handleNavItem = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const isPathSelected = (path) => {
        if (path === '/admin/editorial-intelligence') {
            return location.pathname === path
                || location.pathname.startsWith(`${path}/`)
                || location.pathname === '/admin/config/editorial-intelligence'
                || location.pathname.startsWith('/admin/config/editorial-intelligence/');
        }
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const isCollapsed = sidebarCollapsed && !isMobile;
    const drawerWidth = isMobile ? MOBILE_DRAWER_WIDTH : (isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH);
    const logoSrc = isCollapsed ? COMPACT_LOGO_SRC : NEOFEED_AI_LOGO_SRC;

    const drawerContent = (
        <Box className={`admin-sidebar-content${isCollapsed ? ' admin-sidebar--collapsed' : ''}`}>
            <Box className="admin-sidebar-logo">
                <Box className="admin-sidebar-logo-inner">
                    <img
                        src={logoSrc}
                        alt="NeoFeed AI"
                        className={`admin-logo-img${isCollapsed ? ' admin-logo-img--collapsed' : ''}`}
                    />
                    {!isMobile && (
                        <IconButton
                            onClick={() => setSidebarCollapsed((prev) => !prev)}
                            className="admin-sidebar-collapse-btn"
                            size="small"
                            aria-label={isCollapsed ? 'Expandir menu' : 'Colapsar menu'}
                        >
                            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            <List className="admin-sidebar-menu">
                {MENU_ITEMS.map((item) => (
                    <ListItem key={item.path} disablePadding>
                        <Tooltip title={item.label} placement="right" disableHoverListener={!isCollapsed}>
                            <ListItemButton
                                onClick={() => handleNavItem(item.path)}
                                selected={isPathSelected(item.path)}
                                className="admin-menu-item"
                            >
                                <ListItemIcon className="admin-menu-icon">
                                    {item.icon}
                                </ListItemIcon>
                                {!isCollapsed && <ListItemText primary={item.label} />}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}
            </List>

            <Box sx={{ flexGrow: 1 }} />

            <Box className="admin-sidebar-footer">
                <List>
                    <ListItem disablePadding>
                        <Tooltip title="Sair" placement="right" disableHoverListener={!isCollapsed}>
                            <ListItemButton onClick={handleLogout} className="admin-logout">
                                <ListItemIcon className="admin-menu-icon">
                                    <LogoutIcon />
                                </ListItemIcon>
                                {!isCollapsed && <ListItemText primary="Sair" />}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                </List>
            </Box>
        </Box>
    );

    return (
        <Box className="admin-layout">
            {isMobile && (
                <AppBar position="fixed" className="admin-mobile-appbar">
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="abrir menu"
                            edge="start"
                            onClick={handleDrawerToggle}
                        >
                            <MenuIcon />
                        </IconButton>
                        <img
                            src={NEOFEED_AI_LOGO_SRC}
                            alt="NeoFeed AI"
                            className="admin-mobile-logo"
                        />
                    </Toolbar>
                </AppBar>
            )}

            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? mobileOpen : true}
                onClose={handleDrawerToggle}
                className="admin-drawer"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        transition: 'width 0.2s ease',
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            <Box
                className="admin-main"
                sx={{
                    flexGrow: 1,
                    width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
                    transition: 'width 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    height: isMobile ? 'calc(100dvh - 56px)' : '100dvh',
                    minHeight: 0,
                    mt: isMobile ? '64px' : 0,
                }}
            >
                <Box component="main" className="admin-content" sx={{ flexGrow: 1 }}>
                    <Box className={`admin-content-inner${fullWidth ? ' admin-content-inner--full' : ''}`}>
                        {children}
                    </Box>
                    <ToastContainer />
                </Box>
            </Box>
        </Box>
    );
}
